import h from 'hyperscript';
import GeoJSON from 'ol/format/GeoJSON';
import MultiPoint from 'ol/geom/MultiPoint';
import MultiLineString from 'ol/geom/MultiLineString';
import MultiPolygon from 'ol/geom/MultiPolygon';

import { createFeature, getLayerFeatureAttributes } from 'cadenza/api-client/maps-api';
import { LayerProperty } from 'cadenza/map/layer-property';
import {
  AbstractFeaturePanel,
  NOTIFICATION_TYPE
} from 'cadenza/map/feature-attributes-panel/abstract-feature-panel';
import { refreshLayer } from 'cadenza/map/layer-utils';
import { logger } from 'cadenza/utils/logging';
import {
  createFormGroups,
  getAttributes
} from 'cadenza/map/feature-attributes-panel/feature-attributes-panel';
import { createSnappingControl } from 'cadenza/map/edit-geometry-panel/edit-geometry-panel';

import './create-new-feature-panel.css';
import i18n from './messages.properties';

const NAME = 'd-create-new-feature-panel';

export class CreateNewFeaturePanel extends AbstractFeaturePanel {

  #drawLayerSource;
  #geoJSON;
  #geometryType;
  #formGroups;
  #defaultValuesMap;

  constructor ({ map, layer, drawLayerSource, geometryType, onClose } = {}) {
    super({
      map,
      layer,
      title: i18n('title'),
      onClose,
      shouldConfirmPrimaryInteractionChange: true
    });
    this.#drawLayerSource = drawLayerSource;
    this.#geometryType = geometryType;
    this.#geoJSON = new GeoJSON();
  }

  connectedCallback () {
    super.connectedCallback();
    this.classList.add(NAME);
    const content = this.querySelector('.content');
    content.append(h('label.d-compact-header--title', i18n('title.geometry')));
    this.querySelector('.content')
      .append(createSnappingControl(this.map));
    content.append(h('label.d-compact-header--title', i18n('title.attributes')));
    getLayerFeatureAttributes(this.selectedLayer.get(LayerProperty.ID))
      .then((attributeMetaDataJSON) => {
        this.#defaultValuesMap = getDefaultValuesMap(attributeMetaDataJSON.attributeMetaData);
        this.#formGroups = createFormGroups(attributeMetaDataJSON, false, this.#defaultValuesMap);
        content.append(...this.#formGroups);
      })
      .catch(error => logger.error('Failed to get feature attributes', error));
  }

  disconnectedCallback () {
    super.disconnectedCallback();
    refreshLayer(this.selectedLayer);
  }

  onMapPrimaryInteractionChange () {
    this._createFeature().catch(
      errorResponse => {
        logger.error(this._getErrorMessages(errorResponse?.content?.validationMessages));
      }
    );
    super.close(false);
  }

  _createFeature () {
    const layerId = this.selectedLayer.get(LayerProperty.ID);
    const featureDto = this._getFeatureDto();
    if (featureDto.geometry) {
      return createFeature(layerId, featureDto)
        .then(() => {
          refreshLayer(this.selectedLayer);
        });
    } else {
      return Promise.reject({
        content: {
          validationMessages: [ {
            message: i18n('noGeometryError'),
            type: NOTIFICATION_TYPE.VALIDATOR
          } ]
        }
      });
    }
  }

  _getFeatureDto (featureId) {
    const serializedLayerGeometries = serializeLayerGeometries(this.#drawLayerSource, this.#geometryType);
    const attributes = getAttributes(this.#formGroups, this.#defaultValuesMap);
    return { featureId, attributes, geometry: serializedLayerGeometries };
  }

  onSave (shouldResetInteraction) {
    this._createFeature().then(() => {
      super.onSave(shouldResetInteraction);
    }).catch(errorResponse => this.showErrorsInNotificationArea(this._getErrorMessages(errorResponse?.content?.validationMessages)));
  }

  _getErrorMessages (messages = []) {
    return messages.map(notification => {
      switch (notification.type) {
        case NOTIFICATION_TYPE.VALIDATOR :
          return notification.message;
        case NOTIFICATION_TYPE.GEOMETRY :
          logger.error('Error while saving geometry', notification.message);
          return i18n('featureError');
        default:
          throw new Error(`Error ${JSON.stringify(notification)} not supported`);
      }
    });
  }

}

export function serializeLayerGeometries (layerSource, geometryType) {
  const features = layerSource.getFeatures();
  if (features.length) {
    return new GeoJSON().writeGeometryObject(getGeometry(features, geometryType));
  }
  return null;
}

function getGeometry (features, geometryType) {
  if (geometryType === 'MultiPoint') {
    const points = features.map(feature => {
      const geometry = feature.getGeometry();
      return geometry.getType() === 'MultiPoint' ? geometry.getPoints() : geometry;
    }).flat()
      .map(geometry => geometry.getCoordinates());
    return new MultiPoint(points);
  } else if (geometryType === 'MultiLineString') {
    const lines = features.map(feature => {
      const geometry = feature.getGeometry();
      return geometry.getType() === 'MultiLineString' ? geometry.getLineStrings() : geometry;
    }).flat();
    return new MultiLineString(lines);
  } else if (geometryType === 'MultiPolygon') {
    const polygons = features.map(feature => {
      const geometry = feature.getGeometry();
      return geometry.getType() === 'MultiPolygon' ? geometry.getPolygons() : geometry;
    }).flat();
    return new MultiPolygon(polygons);
  }
  return features[0].getGeometry();
}

customElements.define(NAME, CreateNewFeaturePanel);

function getDefaultValuesMap (attributeMetaData) {
  const attributes = {};
  attributeMetaData.forEach((attributeMetaDataJSONEntry) => {
    attributes[attributeMetaDataJSONEntry.name] = attributeMetaDataJSONEntry.defaultValue;
  });
  return attributes;
}
